import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { fmtDate } from '../../shared/Panel';
import { toErrorMessage } from '../userOffers/orderActionsApi';
import {
  cancelCostRateChange,
  costRateChangeState,
  costRateValueText,
  fetchCostRateHistory,
  type CostRateChange,
} from './costParametersApi';

/**
 * Historia stawek kosztu spółki (prace 24.10.2026, S-24.10-12, przełącznik
 * `parameterOwnership`): było, jest, od kiedy, kto, powód — razem ze
 * zmianami zaplanowanymi (z „Odwołaj”) i odwołanymi. Właściciel stawek
 * w tej samej historii.
 */
export function CostRateHistoryDialog({
  record,
  onClose,
  onChanged,
}: {
  record: Record<string, unknown>;
  onClose: () => void;
  onChanged?: () => void;
}) {
  const id = record.id as number;
  const [rows, setRows] = useState<CostRateChange[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState<CostRateChange | null>(null);

  const load = useCallback(async () => {
    try {
      setRows(await fetchCostRateHistory(id));
      setError(null);
    } catch (e) {
      setError(toErrorMessage(e));
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Historia stawek kosztu — {String(record.name ?? '')}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Właściciel stawek:{' '}
          {typeof record.costRatesOwner === 'string' && record.costRatesOwner !== ''
            ? record.costRatesOwner
            : 'właściciel nieustalony'}
          . Koszt liczy stawka obowiązująca teraz; zmiana zaplanowana wchodzi w życie w swoim
          terminie.
        </Typography>
        {error && <Alert severity="error">{error}</Alert>}
        {rows === null && !error ? (
          <CircularProgress size={24} />
        ) : rows && rows.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Brak zmian zapisanych z powodem.
          </Typography>
        ) : (
          <Table size="small" aria-label="Historia stawek" data-testid="cost-rates-history-table">
            <TableHead>
              <TableRow>
                <TableCell>Kiedy</TableCell>
                <TableCell>Kto</TableCell>
                <TableCell>Pole</TableCell>
                <TableCell>Było → jest</TableCell>
                <TableCell>Obowiązuje od</TableCell>
                <TableCell>Powód</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {(rows ?? []).map((c) => (
                <TableRow key={c.id} data-testid={`cost-rate-change-${c.id}`}>
                  <TableCell>{fmtDate(c.changedAt)}</TableCell>
                  <TableCell>{c.changedByName ?? '—'}</TableCell>
                  <TableCell>{c.label}</TableCell>
                  <TableCell>
                    {c.field === 'owner'
                      ? `${c.oldText ?? 'nieustalony'} → ${c.newText ?? 'nieustalony'}`
                      : `${costRateValueText(c.oldValue, c.oldText)} → ${costRateValueText(c.newValue, c.newText)}`}
                  </TableCell>
                  <TableCell>
                    <Stack spacing={0.5} sx={{ alignItems: 'flex-start' }}>
                      <span>{fmtDate(c.validFrom)}</span>
                      <Chip
                        size="small"
                        variant="outlined"
                        color={c.cancelledAt ? 'default' : c.appliedAt ? 'success' : 'info'}
                        label={costRateChangeState(c)}
                      />
                    </Stack>
                  </TableCell>
                  <TableCell>
                    {c.reason}
                    {c.cancelledAt && (
                      <Typography variant="caption" color="text.secondary" component="div">
                        odwołał(a) {c.cancelledByName ?? '—'} {fmtDate(c.cancelledAt)}: {c.cancelReason}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    {!c.appliedAt && !c.cancelledAt && (
                      <Button
                        size="small"
                        color="warning"
                        onClick={() => setCancelling(c)}
                        data-testid={`cost-rate-cancel-${c.id}`}
                      >
                        Odwołaj
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Zamknij</Button>
      </DialogActions>
      {cancelling && (
        <CancelCostRateDialog
          companyId={id}
          change={cancelling}
          onClose={() => setCancelling(null)}
          onDone={() => {
            setCancelling(null);
            void load();
            onChanged?.();
          }}
        />
      )}
    </Dialog>
  );
}

function CancelCostRateDialog({
  companyId,
  change,
  onClose,
  onDone,
}: {
  companyId: number;
  change: CostRateChange;
  onClose: () => void;
  onDone: () => void;
}) {
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (reason.trim() === '') {
      setError('Podaj powód odwołania');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await cancelCostRateChange(companyId, change.id, reason.trim());
      onDone();
    } catch (e) {
      setError(toErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Odwołać zmianę zaplanowaną?</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2 }}>
          {change.label}: {costRateValueText(change.newValue, change.newText)} od{' '}
          {fmtDate(change.validFrom)}. Zostaje obecna stawka.
        </Typography>
        <TextField
          label="Powód odwołania"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          size="small"
          fullWidth
          multiline
          minRows={2}
          required
          slotProps={{ htmlInput: { maxLength: 500 } }}
        />
        {error && (
          <Typography role="alert" color="error" variant="body2" sx={{ mt: 2 }}>
            {error}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Anuluj
        </Button>
        <Button onClick={save} variant="contained" color="warning" disabled={busy}>
          Odwołaj zmianę
        </Button>
      </DialogActions>
    </Dialog>
  );
}
